import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getCandidates, getJobsByCandidateId } from '../services/api';
import { SparklesIcon } from '@heroicons/react/24/outline';

function JobMatching() {
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [uploadedResume, setUploadedResume] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [viewMode, setViewMode] = useState('candidate'); // 'candidate', 'resume', or 'internal'

  // Internal matching state
  const [talentPoolCandidates, setTalentPoolCandidates] = useState([]);
  const [internalJobs, setInternalJobs] = useState([]);
  const [selectedTalentCandidate, setSelectedTalentCandidate] = useState(null);
  const [selectedInternalJob, setSelectedInternalJob] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [isMatchingLoading, setIsMatchingLoading] = useState(false);

  useEffect(() => {
    // Load candidates when component mounts
    fetchCandidates();
  }, []);

  // Load talent pool and internal jobs when switching to internal mode
  useEffect(() => {
    if (viewMode === 'internal') {
      fetchTalentPoolCandidates();
      fetchInternalJobs();
    }
  }, [viewMode]);

  const fetchTalentPoolCandidates = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const token = user?.token || localStorage.getItem('token');

      const res = await axios.get(`${apiUrl}/api/talent`, {
        params: { limit: 50 },
        headers: { Authorization: `Bearer ${token}` }
      });

      setTalentPoolCandidates(res.data.candidates || []);
    } catch (error) {
      console.error('Error fetching talent pool:', error);
      toast.error('Failed to load talent pool candidates');
    }
  };

  const fetchInternalJobs = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const token = user?.token || localStorage.getItem('token');

      const res = await axios.get(`${apiUrl}/api/jobs`, {
        params: { limit: 50, status: 'Open' },
        headers: { Authorization: `Bearer ${token}` }
      });

      setInternalJobs(res.data.jobs || res.data || []);
    } catch (error) {
      console.error('Error fetching internal jobs:', error);
      toast.error('Failed to load internal jobs');
    }
  };

  const handleInternalMatch = async () => {
    if (!selectedTalentCandidate || !selectedInternalJob) {
      toast.error('Please select both a candidate and a job');
      return;
    }

    setIsMatchingLoading(true);
    setMatchResult(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const token = user?.token || localStorage.getItem('token');

      const res = await axios.post(
        `${apiUrl}/api/match/candidate/${selectedTalentCandidate._id}/job/${selectedInternalJob._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMatchResult(res.data);
      toast.success(`Match score: ${res.data.matchScore}% - ${res.data.recommendation}`);
    } catch (error) {
      console.error('Error generating match:', error);
      toast.error('Failed to generate match score');
    } finally {
      setIsMatchingLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      // Ensure token is set for authentication
      if (!localStorage.getItem('token')) {
        console.warn('No auth token found, this may cause API requests to fail');
      }
      
      const response = await getCandidates();
      // Ensure response.data is an array
      if (response.data && Array.isArray(response.data)) {
        setCandidates(response.data);
      } else if (response.data && Array.isArray(response.data.candidates)) {
        setCandidates(response.data.candidates);
      } else {
        console.error('Candidates data is not an array:', response.data);
        setCandidates([]);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
      setCandidates([]);
    }
  };

  const handleCandidateSelect = async (candidateId) => {
    setIsLoading(true);
    try {
      const candidate = candidates.find(c => c._id === candidateId);
      setSelectedCandidate(candidate);
      
      // Get matching jobs from API
      const response = await getJobsByCandidateId(candidateId);
      setMatchedJobs(response.data.matchedJobs || []);
      
      toast.success('Matched jobs loaded');
    } catch (error) {
      console.error('Error getting matched jobs:', error);
      toast.error('Failed to load matched jobs');
      setMatchedJobs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobSearch = async (e) => {
    e.preventDefault();
    
    setIsLoading(true);
    try {
      if (viewMode === 'resume') {
        // Resume-based job search
        if (!uploadedResume && !resumeText) {
          toast.error('Please upload a resume or enter resume text');
          setIsLoading(false);
          return;
        }
        
        console.log('Using resume-based job search');
        
        // Prepare form data for file upload
        const formData = new FormData();
        
        if (uploadedResume) {
          // Add resume file
          formData.append('resume', uploadedResume);
          console.log(`Added resume file: ${uploadedResume.name}`);
        } else {
          // Add resume text
          formData.append('resumeText', resumeText);
          console.log('Added resume text');
        }
        
        // Add optional search parameters
        if (searchQuery) {
          formData.append('query', searchQuery);
          console.log(`Added additional search query: ${searchQuery}`);
        }
        
        if (searchLocation) {
          formData.append('location', searchLocation);
          console.log(`Added location: ${searchLocation}`);
        }
        
        // Get token for authorization
        const token = localStorage.getItem('token') || 'mock-token-for-development';
        
        // Call the resume matching endpoint
        const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/job-scraping/match-resume`;
        console.log(`Sending resume to ${apiUrl}`);
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        }).then(res => {
          if (!res.ok) {
            console.error('Error response:', res.status);
            console.error('Error statusText:', res.statusText);
            throw new Error(`Server responded with status: ${res.status}`);
          }
          return res.json();
        });
        
        console.log('Resume-based job search response:', response);
        
        // Handle the API response
        if (response.matchedJobs && Array.isArray(response.matchedJobs)) {
          setMatchedJobs(response.matchedJobs);
          
          // Show extracted keywords if available
          if (response.keywords && response.keywords.length > 0) {
            toast.info(`Keywords from resume: ${response.keywords.join(', ')}`);
          }
          
          // Show detected domain if available
          if (response.candidateInfo && response.candidateInfo.domain) {
            toast.info(`Detected domain: ${response.candidateInfo.domain}`);
          }
          
          toast.success(`Found ${response.matchedJobs.length} matching jobs`);
        } else if (response.jobs && Array.isArray(response.jobs)) {
          // Format jobs with consistent structure
          const formattedJobs = response.jobs.map(job => ({
            ...job,
            matchScore: job.matchScore || 100,
            matchAnalysis: job.matchAnalysis || {
              matchScore: 100,
              overallMatch: 'Strong',
              recommendation: 'Strong Match',
              matchReasons: ['Matched resume keywords']
            }
          }));
          
          setMatchedJobs(formattedJobs);
          toast.success(`Found ${formattedJobs.length} matching jobs`);
        } else {
          setMatchedJobs([]);
          toast.warning('No matching jobs found for this resume');
        }
      } else {
        // Keyword-based job search
        if (!searchQuery) {
          toast.error('Please enter search keywords');
          setIsLoading(false);
          return;
        }
        
        console.log(`Searching jobs with query: ${searchQuery}, location: ${searchLocation || 'Remote'}`);
        
        // Call the API directly with keywords and location
        const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/job-scraping/search`;
        const token = localStorage.getItem('token') || 'mock-token-for-development';
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            query: searchQuery,
            location: searchLocation || 'Remote'
          }),
          credentials: 'include'
        }).then(res => {
          if (!res.ok) {
            console.error('Error response:', res.status);
            console.error('Error statusText:', res.statusText);
            throw new Error(`Server responded with status: ${res.status}`);
          }
          return res.json();
        });
        
        console.log('Job search response:', response);
        
        // Handle the API response
        if (response.jobs && Array.isArray(response.jobs)) {
          // Format jobs with consistent structure
          const formattedJobs = response.jobs.map(job => ({
            ...job,
            matchScore: 100, // All direct search jobs are 100% matches
            matchAnalysis: {
              matchScore: 100,
              overallMatch: 'Strong',
              recommendation: 'Strong Match',
              matchReasons: ['Matched search keywords exactly']
            }
          }));
          
          setMatchedJobs(formattedJobs);
          toast.success(`Found ${formattedJobs.length} matching jobs`);
        } else {
          setMatchedJobs([]);
          toast.warning('No matching jobs found');
        }
      }
    } catch (error) {
      console.error('Error searching for jobs:', error);
      toast.error('Failed to search for matching jobs: ' + error.message);
      setMatchedJobs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedResume(file);
      
      // Preview resume text if it's a text file
      if (file.type === 'text/plain') {
        const reader = new FileReader();
        reader.onload = (e) => {
          setResumeText(e.target.result);
        };
        reader.readAsText(file);
      } else {
        setResumeText('');
      }
    }
  };

  const applyToJob = (job) => {
    // Here you would implement job application functionality
    toast.info(`Application feature for job "${job.title}" coming soon!`);
  };

  const getMatchScoreClass = (score) => {
    if (score >= 70) return 'bg-green-100 text-green-800';
    if (score >= 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Job Matching</h1>
      
      {/* View Mode Toggle */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex space-x-4">
          <button
            onClick={() => setViewMode('candidate')}
            className={`py-2 px-4 font-medium rounded ${
              viewMode === 'candidate'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Match by Candidate
          </button>
          <button
            onClick={() => setViewMode('resume')}
            className={`py-2 px-4 font-medium rounded ${
              viewMode === 'resume'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Match by Resume
          </button>
          <button
            onClick={() => setViewMode('internal')}
            className={`py-2 px-4 font-medium rounded flex items-center gap-2 ${
              viewMode === 'internal'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <SparklesIcon className="h-4 w-4" />
            AI Match (Internal)
          </button>
        </div>
      </div>
      
      {/* Candidate Selection (visible in candidate mode) */}
      {viewMode === 'candidate' && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-lg font-semibold mb-4">Select Candidate</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="candidate" className="block text-sm font-medium text-gray-700 mb-1">
                Candidate
              </label>
              <select
                id="candidate"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                onChange={(e) => handleCandidateSelect(e.target.value)}
                value={selectedCandidate?._id || ''}
              >
                <option value="">Select a candidate</option>
                {Array.isArray(candidates) ? candidates.map(candidate => (
                  <option key={candidate._id} value={candidate._id}>
                    {candidate.name} - {candidate.title || 'No title'}
                  </option>
                )) : <option value="" disabled>No candidates available</option>}
              </select>
            </div>
            
            {selectedCandidate && (
              <div className="bg-gray-50 p-3 rounded-md">
                <h3 className="font-medium text-gray-700">Selected Candidate</h3>
                <p><span className="font-semibold">Name:</span> {selectedCandidate.name}</p>
                <p><span className="font-semibold">Title:</span> {selectedCandidate.title || 'N/A'}</p>
                <p><span className="font-semibold">Location:</span> {selectedCandidate.location || 'N/A'}</p>
                <p>
                  <span className="font-semibold">Skills:</span>{' '}
                  {selectedCandidate.skills && selectedCandidate.skills.length > 0
                    ? selectedCandidate.skills.join(', ')
                    : 'No skills listed'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Resume Upload (visible in resume mode) */}
      {viewMode === 'resume' && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-lg font-semibold mb-4">Upload Resume</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="resumeUpload" className="block text-sm font-medium text-gray-700 mb-1">
                Upload Resume (PDF, DOC, TXT)
              </label>
              <input
                type="file"
                id="resumeUpload"
                className="w-full border rounded-md p-2"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleResumeUpload}
              />
              {uploadedResume && (
                <p className="text-sm text-gray-600 mt-1">
                  Uploaded: {uploadedResume.name}
                </p>
              )}
            </div>
            
            <div className="-- or --">
              <div className="flex items-center my-3">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-3 text-gray-500 text-sm">OR</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>
            </div>
            
            <div>
              <label htmlFor="resumeText" className="block text-sm font-medium text-gray-700 mb-1">
                Paste Resume Text
              </label>
              <textarea
                id="resumeText"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                rows="6"
                placeholder="Paste your resume text here..."
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
              ></textarea>
            </div>
          </div>
        </div>
      )}

      {/* Internal AI Matching (visible in internal mode) */}
      {viewMode === 'internal' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <SparklesIcon className="h-5 w-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-purple-900">AI-Powered Job Matching</h2>
            </div>
            <p className="text-sm text-purple-700">
              Select a candidate from your talent pool and a job from your database to get an AI-powered match score with detailed analysis.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Candidate Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Candidate (Talent Pool)
                </label>
                <select
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  onChange={(e) => {
                    const candidate = talentPoolCandidates.find(c => c._id === e.target.value);
                    setSelectedTalentCandidate(candidate);
                    setMatchResult(null);
                  }}
                  value={selectedTalentCandidate?._id || ''}
                >
                  <option value="">Select a candidate...</option>
                  {talentPoolCandidates.map(candidate => (
                    <option key={candidate._id} value={candidate._id}>
                      {candidate.fullName} - {candidate.domain || candidate.desiredRoles || 'No role specified'}
                    </option>
                  ))}
                </select>

                {selectedTalentCandidate && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-md text-sm">
                    <p><strong>Name:</strong> {selectedTalentCandidate.fullName}</p>
                    <p><strong>Domain:</strong> {selectedTalentCandidate.domain || 'Not classified'}</p>
                    <p><strong>Skills:</strong> {selectedTalentCandidate.skills?.slice(0, 5).join(', ') || 'None listed'}</p>
                    {selectedTalentCandidate.summary && (
                      <p className="mt-2 text-gray-600 italic">"{selectedTalentCandidate.summary.substring(0, 100)}..."</p>
                    )}
                  </div>
                )}
              </div>

              {/* Job Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Job (Internal Database)
                </label>
                <select
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  onChange={(e) => {
                    const job = internalJobs.find(j => j._id === e.target.value);
                    setSelectedInternalJob(job);
                    setMatchResult(null);
                  }}
                  value={selectedInternalJob?._id || ''}
                >
                  <option value="">Select a job...</option>
                  {internalJobs.map(job => (
                    <option key={job._id} value={job._id}>
                      {job.title} - {job.company || 'No company'}
                    </option>
                  ))}
                </select>

                {selectedInternalJob && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-md text-sm">
                    <p><strong>Title:</strong> {selectedInternalJob.title}</p>
                    <p><strong>Company:</strong> {selectedInternalJob.company || 'Not specified'}</p>
                    <p><strong>Location:</strong> {selectedInternalJob.location || 'Remote'}</p>
                    <p><strong>Industry:</strong> {selectedInternalJob.industry || 'Not specified'}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <button
                onClick={handleInternalMatch}
                disabled={!selectedTalentCandidate || !selectedInternalJob || isMatchingLoading}
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <SparklesIcon className="h-5 w-5" />
                {isMatchingLoading ? 'Analyzing Match...' : 'Generate AI Match Score'}
              </button>
            </div>
          </div>

          {/* Match Result */}
          {matchResult && (
            <div className="bg-white p-6 rounded-lg shadow border-2 border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Match Analysis Result</h3>
                <span className={`px-4 py-2 rounded-full text-lg font-bold ${
                  matchResult.matchScore >= 70 ? 'bg-green-100 text-green-800' :
                  matchResult.matchScore >= 40 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {matchResult.matchScore}%
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Candidate</p>
                  <p className="font-medium">{matchResult.candidateName}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Job</p>
                  <p className="font-medium">{matchResult.jobTitle}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Recommendation</p>
                  <p className={`font-medium ${
                    matchResult.recommendation === 'Strong Match' ? 'text-green-600' :
                    matchResult.recommendation === 'Moderate Match' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {matchResult.recommendation}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">AI Analysis</h4>
                <ul className="space-y-2">
                  {matchResult.matchSummary?.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-purple-500 mt-1">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {matchResult.metadata && (
                <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                  <p>Generated at: {new Date(matchResult.metadata.generatedAt).toLocaleString()}</p>
                  <p>Candidate Skills: {matchResult.metadata.candidateSkillsCount} | Domain: {matchResult.metadata.candidateDomain}</p>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!matchResult && !isMatchingLoading && (
            <div className="bg-gray-50 p-8 rounded-lg text-center border border-dashed border-gray-300">
              <SparklesIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">
                Select a candidate and job above, then click "Generate AI Match Score" to see the analysis.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Job Search Form (hidden in internal mode) */}
      {viewMode !== 'internal' && (
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">Search for Jobs</h2>
        <form onSubmit={handleJobSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="searchQuery" className="block text-sm font-medium text-gray-700 mb-1">
                Search Keywords (optional)
              </label>
              <input
                type="text"
                id="searchQuery"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="e.g. Software Engineer, Developer"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="searchLocation" className="block text-sm font-medium text-gray-700 mb-1">
                Location (optional)
              </label>
              <input
                type="text"
                id="searchLocation"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="e.g. San Francisco, Remote"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {isLoading ? 'Searching...' : 'Find Matching Jobs'}
            </button>
          </div>
        </form>
      </div>
      )}

      {/* Matched Jobs Results (hidden in internal mode) */}
      {viewMode !== 'internal' && matchedJobs.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">Matched Jobs ({matchedJobs.length})</h2>
          
          <div className="space-y-6">
            {matchedJobs.map((job) => (
              <div key={job._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold">{job.title}</h3>
                    <p className="text-gray-700">{job.company}</p>
                  </div>
                  
                  {job.matchScore !== undefined && (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getMatchScoreClass(job.matchScore)}`}>
                      Match: {job.matchScore}%
                    </span>
                  )}
                </div>
                
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Location:</span> {job.location}
                    </p>
                    {job.salary && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Salary:</span> {job.salary}
                      </p>
                    )}
                    {job.source && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Source:</span> {job.source}
                      </p>
                    )}
                  </div>
                  
                  {job.matchAnalysis && job.matchAnalysis.matchReasons && (
                    <div className="text-sm">
                      <span className="font-medium">Match Reasons:</span>
                      <ul className="list-disc list-inside mt-1">
                        {job.matchAnalysis.matchReasons.slice(0, 2).map((reason, idx) => (
                          <li key={idx} className="text-gray-600">{reason}</li>
                        ))}
                        {job.matchAnalysis.matchReasons.length > 2 && (
                          <li className="text-gray-600">
                            +{job.matchAnalysis.matchReasons.length - 2} more reasons
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="mt-3 space-y-2">
                  {job.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {job.description.substring(0, 200)}
                      {job.description.length > 200 ? '...' : ''}
                    </p>
                  )}
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => applyToJob(job)}
                      className="px-3 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700"
                    >
                      Apply
                    </button>
                    
                    {job.sourceUrl && (
                      <a
                        href={job.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-gray-200 text-gray-800 text-sm rounded hover:bg-gray-300"
                      >
                        View Original
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* No Results Message (hidden in internal mode) */}
      {viewMode !== 'internal' && !isLoading && matchedJobs.length === 0 && (
        <div className="bg-gray-50 p-8 rounded-lg text-center border">
          <p className="text-gray-600">
            No matched jobs found. Try adjusting your search criteria or selecting a different candidate.
          </p>
        </div>
      )}
    </div>
  );
}

export default JobMatching;